import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api, { assetUrl } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Trash2, Palette, Send, EyeOff, CheckCircle2, Clock3, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import { useLanguage } from '@/context/LanguageContext';

const MyDesigns = () => {
  const [designs, setDesigns] = useState([]);
  const [publishOpen, setPublishOpen] = useState(false);
  const [target, setTarget] = useState(null);
  const [form, setForm] = useState({ title: '', description: '', tags: '', category: 'sticker' });
  const { t } = useLanguage();

  const STATUS_MAP = {
    draft: { label: t('mydesigns.statusDraft', 'Draft'), className: 'bg-white/[0.06] text-white/80 border-white/10', icon: Palette },
    pending: { label: t('mydesigns.statusPending', 'Pending review'), className: 'bg-amber-500/15 text-amber-200 border-amber-400/25', icon: Clock3 },
    approved: { label: t('mydesigns.statusApproved', 'Live in community'), className: 'bg-emerald-500/15 text-emerald-200 border-emerald-400/25', icon: CheckCircle2 },
    rejected: { label: t('mydesigns.statusRejected', 'Needs changes'), className: 'bg-rose-500/15 text-rose-200 border-rose-400/25', icon: XCircle },
  };

  const CATEGORY_OPTIONS = [
    { value: 'sticker', label: t('ai.catSticker', 'Sticker') },
    { value: 'phone_case', label: t('ai.catPhoneCase', 'Phone Case') },
    { value: 'label', label: t('ai.catLabel', 'Label') },
  ];

  const load = () => api.get('/user-designs').then(({ data }) => setDesigns(data));
  useEffect(() => { load(); }, []);

  const remove = async (id) => {
    try {
      await api.delete(`/user-designs/${id}`);
      toast.success('Deleted');
      load();
    } catch { toast.error('Failed'); }
  };

  const openPublish = (d) => {
    setTarget(d);
    setForm({
      title: d.listing_title || d.name || '',
      description: d.listing_description || '',
      tags: (d.listing_tags || []).join(', '),
      category: d.listing_category || d.product_type || 'sticker',
    });
    setPublishOpen(true);
  };

  const submitPublish = async () => {
    if (!target) return;
    const tags = form.tags.split(',').map((t) => t.trim()).filter(Boolean).slice(0, 8);
    try {
      await api.post(`/user-designs/${target.id}/publish`, {
        title: form.title.trim() || undefined,
        description: form.description.trim() || undefined,
        tags,
        category: form.category,
      });
      toast.success(t('mydesigns.submittedToast', 'Submitted for review'));
      setPublishOpen(false);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to submit');
    }
  };

  const unpublish = async (d) => {
    try {
      await api.post(`/user-designs/${d.id}/unpublish`);
      toast(t('mydesigns.hiddenToast', 'Listing hidden from community'));
      load();
    } catch { toast.error('Failed'); }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
      <div className="flex items-baseline justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl sm:text-4xl font-bold">{t('mydesigns.title', 'My Designs')}</h1>
          <p className="mt-1" style={{ color: 'var(--jw-text-muted)' }}>
            {t('mydesigns.subtitle', 'Reuse, add to cart, or publish your designs to the community — admin will review before going live.')}
          </p>
        </div>
        <Link to="/community"><Button variant="secondary" data-testid="mydesigns-community-link" className="bg-white/[0.06] text-white border border-white/10 hover:bg-white/[0.09]">{t('mydesigns.browseCommunity', 'Browse community')}</Button></Link>
      </div>

      {designs.length === 0 ? (
        <div className="jw-card p-16 text-center mt-8">
          <Palette className="w-8 h-8 mx-auto" style={{ color: 'var(--jw-text-subtle)' }} />
          <p className="mt-3" style={{ color: 'var(--jw-text-muted)' }}>{t('mydesigns.empty', 'No saved designs yet.')}</p>
          <Link to="/shop"><Button data-testid="mydesigns-shop-button" className="mt-4 jw-btn-primary">{t('mydesigns.startDesigning', 'Start designing')}</Button></Link>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 mt-8">
          {designs.map((d) => {
            const status = STATUS_MAP[d.moderation_status || 'draft'];
            const StatusIcon = status.icon;
            const isPending = d.moderation_status === 'pending';
            const isApproved = d.moderation_status === 'approved';
            return (
              <Card key={d.id} className="jw-card overflow-hidden flex flex-col" data-testid={`mydesigns-card-${d.id}`}>
                <div className="aspect-square overflow-hidden p-2" style={{ background: 'var(--jw-bg-elev)' }}>
                  <img src={assetUrl(d.preview_url) || '/favicon.svg'} onError={(e) => { e.currentTarget.src = '/favicon.svg'; }} alt={d.name} className="w-full h-full object-contain" />
                </div>
                <div className="p-3 flex flex-col gap-2 flex-1">
                  <div className="flex items-center justify-between gap-2">
                    <p className="font-semibold text-sm truncate">{d.listing_title || d.name}</p>
                    <button onClick={() => remove(d.id)} data-testid={`mydesigns-delete-${d.id}-button`} className="p-1 rounded" style={{ color: '#E11D48' }} aria-label="Delete"><Trash2 className="w-4 h-4" /></button>
                  </div>
                  <p className="text-xs capitalize" style={{ color: 'var(--jw-text-subtle)' }}>{(d.product_type || '').replace('_', ' ')}</p>
                  <Badge className={`self-start text-[10px] border inline-flex items-center gap-1 ${status.className}`} data-testid={`mydesigns-status-${d.id}`}>
                    <StatusIcon className="w-3 h-3" /> {status.label}
                  </Badge>
                  {d.moderation_status === 'rejected' && d.moderation_notes && (
                    <p className="text-[11px] p-2 rounded bg-rose-500/10 border border-rose-400/25" style={{ color: 'var(--jw-text-muted)' }}>
                      <b>Admin:</b> {d.moderation_notes}
                    </p>
                  )}
                  {d.auto_moderation && d.auto_moderation.verdict === 'warn' && d.moderation_status === 'pending' && (
                    <p className="text-[11px] p-2 rounded bg-amber-500/10 border border-amber-400/25" style={{ color: 'var(--jw-text-muted)' }} data-testid={`mydesigns-warn-${d.id}`}>
                      Our auto-moderation flagged this for closer review. It's still queued.
                    </p>
                  )}
                  <div className="mt-auto flex flex-wrap gap-2 pt-2">
                    {!isApproved && !isPending && (
                      <Button size="sm" onClick={() => openPublish(d)} data-testid={`mydesigns-publish-${d.id}-button`} className="jw-btn-glossy h-8 rounded-md flex-1">
                        <Send className="w-3.5 h-3.5 mr-1" /> {t('mydesigns.publish', 'Publish')}
                      </Button>
                    )}
                    {isPending && (
                      <Button size="sm" onClick={() => unpublish(d)} data-testid={`mydesigns-cancel-${d.id}-button`} variant="secondary" className="h-8 rounded-md flex-1 bg-white/[0.06] text-white border border-white/10 hover:bg-white/[0.09]">
                        <EyeOff className="w-3.5 h-3.5 mr-1" /> {t('mydesigns.cancelSubmission', 'Cancel submission')}
                      </Button>
                    )}
                    {isApproved && (
                      <Button size="sm" onClick={() => unpublish(d)} data-testid={`mydesigns-hide-${d.id}-button`} variant="secondary" className="h-8 rounded-md flex-1 bg-white/[0.06] text-white border border-white/10 hover:bg-white/[0.09]">
                        <EyeOff className="w-3.5 h-3.5 mr-1" /> {t('mydesigns.takeOffline', 'Take offline')}
                      </Button>
                    )}
                    {isApproved && (
                      <Link to={`/community/${d.id}`} className="flex-1"><Button size="sm" data-testid={`mydesigns-view-${d.id}-button`} className="jw-btn-glossy h-8 rounded-md w-full">{t('mydesigns.viewListing', 'View listing')}</Button></Link>
                    )}
                    {d.moderation_status === 'rejected' && (
                      <Button size="sm" onClick={() => openPublish(d)} data-testid={`mydesigns-resubmit-${d.id}-button`} className="jw-btn-glossy h-8 rounded-md flex-1">
                        <Send className="w-3.5 h-3.5 mr-1" /> {t('mydesigns.resubmit', 'Resubmit')}
                      </Button>
                    )}
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      <Dialog open={publishOpen} onOpenChange={setPublishOpen}>
        <DialogContent data-testid="mydesigns-publish-dialog" className="max-w-lg">
          <DialogHeader>
            <DialogTitle>{t('mydesigns.publishTitle', 'Publish to community')}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <p className="text-xs" style={{ color: 'var(--jw-text-muted)' }}>
              {t('mydesigns.publishNote', 'Once submitted, an admin will review your listing. Approved listings appear in the community feed and can be liked, saved and rated by others.')}
            </p>
            <div>
              <label className="text-xs" style={{ color: 'var(--jw-text-muted)' }}>{t('mydesigns.formTitle', 'Title')}</label>
              <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Give your design a name" data-testid="publish-title-input" />
            </div>
            <div>
              <label className="text-xs" style={{ color: 'var(--jw-text-muted)' }}>{t('mydesigns.formDescription', 'Description')}</label>
              <Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="What makes this design special?" data-testid="publish-description-input" />
            </div>
            <div>
              <label className="text-xs" style={{ color: 'var(--jw-text-muted)' }}>{t('mydesigns.formTags', 'Tags (comma separated)')}</label>
              <Input value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} placeholder="cute, minimal, floral" data-testid="publish-tags-input" />
            </div>
            <div>
              <label className="text-xs" style={{ color: 'var(--jw-text-muted)' }}>{t('mydesigns.formCategory', 'Category')}</label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="publish-category-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {CATEGORY_OPTIONS.map((o) => (<SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setPublishOpen(false)}>{t('common.cancel', 'Cancel')}</Button>
            <Button onClick={submitPublish} data-testid="publish-submit-button" className="jw-btn-glossy">{t('mydesigns.formSubmit', 'Submit for review')}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default MyDesigns;
